import React, { useState, useEffect } from "react";
import { Heart, MapPin, Clock, Shield, Crown, Circle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ProfileData } from "@/types/profile";
import { useNavigate } from "react-router-dom";
import { FeedProfileThumbnail } from "./FeedProfileThumbnail";
import { businessRulesEngine } from "@/core/BusinessRulesEngine";
import type { BadgeIcon, ProfileStatus } from "@/core/BusinessRulesEngine/types";
import { useAuth } from "@/hooks/useAuth";
import { SortOption } from "@/utils/profileUtils";

interface ProfileCardProps {
  profile: ProfileData;
  onLike: (id: string) => void;
  connectionStatus: 'none' | 'sent' | 'received' | 'matched';
  canLike?: boolean;
  sortBy: SortOption;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  onLike,
  connectionStatus,
  canLike = true,
  sortBy
}) => {
  const navigate = useNavigate();
  const { profileData: currentUserProfile } = useAuth();
  
  // State para o status do perfil
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>({
    badges: [],
    borderStyle: {
      containerClass: 'border border-white/10',
      priority: 6,
      label: 'Padrão'
    }
  });

  // Carregar status do perfil
  useEffect(() => {
    businessRulesEngine.getProfileStatus(profile).then(setProfileStatus);
  }, [profile]);
  
  const formatLastSeen = (lastSeen: string | null, isOnline: boolean | null) => {
    if (isOnline) return 'Online';
    if (!lastSeen) return '';
    try {
      const lastSeenDate = new Date(lastSeen);
      const now = new Date();
      const diffMs = now.getTime() - lastSeenDate.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 5) return 'Online';
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffMinutes < 60) return `Ativo há ${diffMinutes}min`;
      if (diffHours < 24) return `Ativo há ${diffHours}h`;
      // Não mostrar badge se tiver mais de 24 horas
      return '';
    } catch {
      return '';
    }
  };

  const formatLocationDisplay = (
    profileCity: string | null,
    profileState: string | null,
    distance: string,
    currentUserCity: string | null,
    currentUserState: string | null
  ): { text: string; showIcon: boolean } => {
    
    // Regra 1: Mesma cidade E estado → apenas distância (sem ícone)
    if (profileCity === currentUserCity && profileState === currentUserState) {
      return { 
        text: distance || 'Mesma localização', 
        showIcon: false 
      };
    }
    
    // Regra 2: Cidade diferente, mesmo estado → distância - cidade
    if (profileState === currentUserState && profileCity !== currentUserCity) {
      const cityName = profileCity || 'Cidade desconhecida';
      return { 
        text: distance ? `${distance} - ${cityName}` : cityName,
        showIcon: true 
      };
    }
    
    // Regra 3: Estado diferente → cidade - estado
    const cityName = profileCity || 'Cidade desconhecida';
    const stateName = profileState || 'UF';
    return { 
      text: `${cityName} - ${stateName}`,
      showIcon: true 
    };
  };

  const formatDistance = (distance: number | null) => {
    if (distance === null || distance === undefined) return '';
    return distance < 1 ? '< 1 km' : `${Math.round(distance)} km`;
  };

  const calculateAge = (birthDate: string | null): number => {
    if (!birthDate) return 0;
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birth.getDate()) {
        age--;
      }
      return age;
    } catch {
      return 0;
    }
  };

  const getTimeSinceJoined = (createdAt: string | null): string => {
    if (!createdAt) return 'Entrou recentemente';
    
    try {
      const created = new Date(createdAt);
      const now = new Date();
      const diffMs = now.getTime() - created.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Entrou hoje';
      if (diffDays === 1) return 'Entrou há 1 dia';
      if (diffDays < 7) return `Entrou há ${diffDays} dias`;
      
      const diffWeeks = Math.floor(diffDays / 7);
      if (diffWeeks === 1) return 'Entrou há 1 semana';
      if (diffWeeks < 4) return `Entrou há ${diffWeeks} semanas`;
      
      const diffMonths = Math.floor(diffDays / 30);
      if (diffMonths === 1) return 'Entrou há 1 mês';
      return `Entrou há ${diffMonths} meses`;
    } catch {
      return 'Entrou recentemente';
    }
  };

  const age = profile.age || 0;
  const distance = formatDistance(profile.distance);
  const lastSeenText = formatLastSeen(profile.last_seen, profile.is_online);
  const locationInfo = formatLocationDisplay(
    profile.city,
    profile.state_code,
    distance,
    currentUserProfile?.city || null,
    currentUserProfile?.state_code || null
  );

  // Decidir qual informação mostrar baseado no filtro ativo
  const showTimeJoined = sortBy === 'new';
  const displayText = showTimeJoined ? getTimeSinceJoined(profile.created_at) : locationInfo.text;
  const DisplayIcon = showTimeJoined ? Clock : MapPin;

  const handleCardClick = () => {
    if (profile.username) {
      navigate(`/main/user/${profile.username}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canLike) {
      onLike(profile.id);
    }
  };
  
  const getIcon = (iconName: BadgeIcon) => {
    switch(iconName) {
      case 'Shield': return Shield;
      case 'Crown': return Crown;
      case 'Circle': return Circle;
      default: return Shield;
    }
  };

  // Verificar se tem badge de verificado
  const verifiedBadge = profileStatus.badges.find(b => b.type === 'verified');
  const adminBadge = profileStatus.badges.find(b => b.type === 'admin');

  const getStatusBadges = () => {
    const badges = [];
    
    // Badge de atividade online (sem escudo - escudo vai ao lado do nome agora)
    if (lastSeenText) {
      badges.push(
        <Badge key="active" className={`${getLastSeenBadgeStyle()} text-xs flex items-center gap-1 px-2 py-1`}>
          <Clock className="h-3 w-3" />
          {lastSeenText}
        </Badge>
      );
    }
    
    // Badge Premium (tratamento especial com texto)
    const premiumBadge = profileStatus.badges.find(b => b.type === 'premium');
    if (premiumBadge) {
      badges.push(
        <Badge key="premium" className="bg-luxury-gold/90 text-black text-xs flex items-center gap-1 px-2 py-1">
          <Crown className="h-3 w-3" />
          <span className="font-medium">Premium</span>
        </Badge>
      );
    }
    
    return badges.length > 0 ? (
      <div className="absolute top-2 left-2 z-20 flex flex-col gap-1">
        {badges}
      </div>
    ) : null;
  };

  const getAdminBadge = () => {
    // Admin badge permanece no canto superior direito (sem verificado, que foi integrado)
    if (adminBadge) {
      const Icon = getIcon(adminBadge.icon);
      return (
        <div className="absolute top-2 right-2 z-30">
          <div 
            className={`${adminBadge.bgColor} rounded-full p-1.5 border-2 border-white ${adminBadge.animation || ''}`}
            style={{ boxShadow: adminBadge.glowEffect }}
          >
            <Icon className={`h-4 w-4 ${adminBadge.color} fill-current`} />
          </div>
        </div>
      );
    }
    return null;
  };

  // Determinar classe de borda do card (sem borda de conexão - usa apenas status do plano)
  const getCardBorderClass = () => {
    return profileStatus.borderStyle.containerClass;
  };

  const getLastSeenBadgeStyle = () => {
    if (profile.is_online || lastSeenText === 'Online') {
      return "bg-green-500/80 text-white";
    }
    return "bg-black/60 text-white";
  };

  return (
    <Card onClick={handleCardClick} className={`bg-black/40 overflow-hidden transition-all group cursor-pointer py-0 ${getCardBorderClass()} ${profileStatus.borderStyle.animation || ''} hover:border-white/20`}>
      <div className="relative aspect-square overflow-hidden">
        {/* Thumbnail do perfil */}
        <FeedProfileThumbnail
          src={profile.main_photo_url || ''}
          alt={profile.username || 'Profile'}
          username={profile.username}
          className="transition-transform group-hover:scale-105"
        />
        
        {/* Status badges (online, premium) - top left */}
        {getStatusBadges()}
        
        {/* Admin badge - top right corner */}
        {getAdminBadge()}
        
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none z-10" />
        
        {/* Profile info overlay */}
        <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4 text-white z-20 pointer-events-none">
          {/* Linha 1: Coração (match) + Verificado + Nome + Idade */}
          <div className="flex items-center gap-1 mb-1 line-clamp-1">
            {/* Coração de match PRIMEIRO - com animação heartbeat */}
            {connectionStatus === 'matched' && (
              <Heart className="h-3.5 w-3.5 text-luxury-pinkBright fill-current flex-shrink-0 animate-heartbeat" />
            )}
            {/* Escudo de verificado */}
            {verifiedBadge && (
              <Shield className="h-3.5 w-3.5 text-blue-400 fill-current flex-shrink-0" />
            )}
            <h3 className="text-sm md:text-base font-bold truncate">
              {profile.username || 'Usuário'}{age > 0 ? ',' : ''}
            </h3>
            {age > 0 && (
              <span className="text-sm font-normal text-white/90 flex-shrink-0 whitespace-nowrap">{age}</span>
            )}
          </div>
          
          {/* Linha 2: Localização ou Tempo desde criação */}
          <div className="flex items-center gap-2 text-xs text-white/70">
            <div className="flex items-center gap-1 truncate">
              <DisplayIcon className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{displayText}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ProfileCard;
